import React, { useState, useEffect, useRef } from 'react'
import './page.css'
import { AiOutlineTeam } from "react-icons/ai";
import { FaRocket, FaLightbulb, FaHandshake, FaUsers, FaChartLine } from "react-icons/fa";
import { MdOutlineEngineering } from "react-icons/md";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { BiCodeAlt, BiMobile, BiBrain, BiSupport } from "react-icons/bi";
import { GoDotFill } from "react-icons/go";
import { HiOutlineAcademicCap } from "react-icons/hi";
import { CiMail } from "react-icons/ci";
import { FaWhatsapp } from "react-icons/fa";

const AboutUs = () => {
  return (
    <>
      <div id="about-us" className="bg-light">
        <section className="about-page">
          {/* Hero Header */}
          <div className="about-header">
            <div className="container">
              <h1>
                <AiOutlineTeam />&nbsp;About CodeFlex
              </h1>
              <p className='text-start px-4'>
                Welcome to CodeFlex — where innovation meets code. Founded and led by Kalana Udara, 
                we are committed to delivering high-quality, future-ready software solutions.
              </p>
            </div>
          </div>

          <div className="container pb-5">
            {/* Who We Are Section */}
            <div className="about-section text-start">
              <div className="section-title-bordered blue">
                <h2>Who We Are</h2>
              </div>
              <p className="section-description px-1">
                CodeFlex is a Colombo, Sri Lanka–based software solutions company. Under the leadership of 
                <strong> Kalana Udara</strong>, our team specializes in web development, mobile apps, machine learning, 
                and more. We combine technical excellence with creative problem solving to build systems that make a difference.
              </p>
            </div>

            {/* Founder Section */}
            <div className="about-section text-start">
              <div className="section-title-bordered purple">
                <h2>Our Founder & Vision</h2>
              </div>
              
              <div className="founder-card">
                <div className="founder-icon">
                  <HiOutlineAcademicCap />
                </div>
                <div className="founder-content">
                  <h3>Kalana Udara (keudaz)</h3>
                  <p className="founder-subtitle">BSc (Hons) in Software Engineering - SLIIT</p>
                  <p>
                    Kalana Udara is the visionary behind CodeFlex. With deep experience in software engineering, 
                    hardware, and emerging technologies, his passion spans web apps, native & cross-platform mobile 
                    development (Flutter / React Native), machine learning, and electronics.
                  </p>
                  <p className="founder-mission">
                    <FaRocket className="inline-icon" />
                    Kalana leads CodeFlex from Colombo, guiding the company's mission to empower businesses with technology.
                  </p>
                </div>
              </div>
            </div>

            {/* Mission & Values Section */}
            <div className="about-section text-start">
              <div className="section-title-bordered green">
                <h2>Mission & Values</h2>
              </div>

              <div className="mission-box">
                <div className="mission-icon">
                  <FaLightbulb />
                </div>
                <div className="mission-content">
                  <h3>Our Mission</h3>
                  <p>
                    To empower organizations with streamlined, scalable software that drives growth, 
                    efficiency, and innovation.
                  </p>
                </div>
              </div>

              <h3 className="values-title">Core Values</h3>

              <div className="values-grid">
                <div className="value-card">
                  <IoMdCheckmarkCircleOutline className="value-icon" />
                  <h4>Integrity</h4>
                  <p>Honest, ethical relationships with clients</p>
                </div>

                <div className="value-card">
                  <FaRocket className="value-icon" />
                  <h4>Excellence</h4>
                  <p>Commitment to quality and pushing boundaries</p>
                </div>

                <div className="value-card">
                  <FaHandshake className="value-icon" />
                  <h4>Collaboration</h4>
                  <p>We work with clients, not just for them</p>
                </div>

                <div className="value-card">
                  <FaUsers className="value-icon" />
                  <h4>User-First Thinking</h4>
                  <p>Every solution is built with the end user in mind</p>
                </div>

                <div className="value-card">
                  <FaChartLine className="value-icon" />
                  <h4>Continuous Learning</h4>
                  <p>We stay current with new tools, frameworks, and trends</p>
                </div>
              </div>
            </div>

            {/* What We Do Section */}
            <div className="about-section text-start">
              <div className="section-title-bordered orange">
                <h2>What We Do</h2>
              </div>
              <p className="section-description px-1">
                From concept to launch, we deliver comprehensive software solutions tailored to your needs.
              </p>

              <div className="service-list">
                <div className="service-item">
                  <BiCodeAlt className="service-icon" />
                  <div className="service-content">
                    <h4>Custom Software Development</h4>
                    <p>From concept to launch, tailored to fit your needs</p>
                  </div>
                </div>

                <div className="service-item">
                  <BiMobile className="service-icon" />
                  <div className="service-content">
                    <h4>Web & Mobile Apps</h4>
                    <p>Responsive web, native & cross-platform mobile (Flutter / React Native)</p>
                  </div>
                </div>

                <div className="service-item">
                  <BiBrain className="service-icon" />
                  <div className="service-content">
                    <h4>AI / ML Solutions</h4>
                    <p>Integrating intelligence into applications</p>
                  </div>
                </div>

                <div className="service-item">
                  <MdOutlineEngineering className="service-icon" />
                  <div className="service-content">
                    <h4>Consulting & Strategy</h4>
                    <p>Architecture, roadmap, tech decisions</p>
                  </div>
                </div>

                <div className="service-item">
                  <BiSupport className="service-icon" />
                  <div className="service-content">
                    <h4>Maintenance & Support</h4>
                    <p>We stay by your side as your product evolves</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose Us Section */}
            <div className="about-section text-start">
              <div className="section-title-bordered blue">
                <h2>Why Choose CodeFlex</h2>
              </div>

              <ul className="why-list">
                <li className="why-item">
                  <GoDotFill className="dot-icon" />
                  <div>
                    <strong>Founder-Led Integrity</strong> — Direct leadership from Kalana ensures clarity and accountability
                  </div>
                </li>
                <li className="why-item">
                  <GoDotFill className="dot-icon" />
                  <div>
                    <strong>Technical Breadth</strong> — We cover a wide spectrum, from ML & mobile to hardware insights
                  </div>
                </li>
                <li className="why-item">
                  <GoDotFill className="dot-icon" />
                  <div>
                    <strong>Client Focus</strong> — Your vision drives our process
                  </div>
                </li>
                <li className="why-item">
                  <GoDotFill className="dot-icon" />
                  <div>
                    <strong>Ongoing Support</strong> — We don't just deliver — we partner for the long term
                  </div>
                </li>
              </ul>
            </div>

            {/* Journey & Aspirations */}
            <div className="journey-section text-start">
              <div className="journey-icon">
                <FaRocket />
              </div>
              <div className="journey-content">
                <h2>Our Journey & Aspirations</h2>
                <p>
                  Since inception, CodeFlex has grown steadily, helping clients across industries build impactful 
                  platforms. Under Kalana's leadership, we aim to expand our reach, explore newer domains 
                  (like IoT, advanced ML, AR/VR), and maintain the personal touch that defines us.
                </p>
              </div>
            </div>

            {/* Contact Section */}
            <div className="contact-section text-start">
              <i className="bi bi-envelope-fill main-icon"></i>
              <h2><CiMail />&nbsp;Get In Touch</h2>
              <div className='px-5'>
                <p>Ready to bring your vision to life? Let's talk.</p>
                <a href="mailto:support@codeflexofficial.com" className="contact-email-btn">
                  support@codeflexofficial.com
                </a>
                <br/><br/>
                <a
                  href="https://wa.me/94712129185"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-email-btn"
                  style={{ color: "#25D366" }}
                >
                  <FaWhatsapp />&nbsp;+94 71 21 29 185
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default AboutUs;
